import { Component, Inject, ChangeDetectorRef } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, takeUntil } from 'rxjs/operators';

// INTERFACES
import { IFilterConfig } from '../../../filter/interfaces/filter.interface';
import { InvoiceConfig } from '../../../invoice/config/invoice.config';
import { EWarehouseDefaultGroup } from '../../../invoice/interfaces/invoice.interface';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

import { OptionInterface } from './../../../shared/interfaces/common.interface';

// CONFIG

// SERVICES

interface ISearchParams {
  sku :string;
  product_category ?:string;
  sub_category ?:string;
  tags ?:string;
  client_id ?:string;
  warehouse_group_id ?:string;
  supplierId ?:string;
}

@Component({
  selector: 'lum-rfq-line-items-form',
  templateUrl: './rfq-line-items-form.component.html',
  styleUrls: ['./rfq-line-items-form.component.scss'],
})
export class RfqLineItemsFormComponent {
  loading$ :Observable<boolean>;
  form :UntypedFormGroup;
  lineItemFilters :OptionInterface[] = InvoiceConfig.lineItemfilterList;
  filters :IFilterConfig[] = InvoiceConfig.lineItemFiltersConfig;
  relations :string = [
    'category',
    'subcategory',
    'basePrice',
    'tags.tag_details',
    'images',
    'locationStock',
    'variantParent.category',
    'variantParent.subcategory',
    'variantParent.tags.tag_details',
    'variantParent.unitOfMeasures',
    'rfqAttachments',
    'unitOfMeasures',
    'quotations',
  ].join(',');

  totalRfq :number = 0;
  searchSubscription :any | null = null; // TODO: Move or implement in service
  rfqs$ :Observable<any[] | null>; // TODO: Create type

  private searchUpdated$ = new Subject<ISearchParams>();
  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data :{
      warehouse_group_id :number;
      rfq_type :string;
      client_purchase_order_type_id :number;
      client_id :number;
      supplierId :number;
      warehouseId :number;
    },
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private dialogRef :MatDialogRef<RfqLineItemsFormComponent>,
    private rfqApiService :RfqApiService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.getFromService();
    this.getRfqs().subscribe();
    this.onSearchUpdate().subscribe();
  }
  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    if (this.searchSubscription) {
      // TODO: Move or implement in service
      this.searchSubscription?.unsubscribe();
    }
  }

  onQueryBuild(event :any) :void {
    const { query } = event?.target?.value || event;
    this.searchUpdated$.next(query);
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSubmit() :void {
    const lineItems = this.selectedLineItemsFormArray.value.filter(
      (rfq :any) => rfq.selected,
    );
    this.dialogRef.close(lineItems);
  }

  private initActions() :void {
    this.rfqApiService.search({
      sku: '',
      relations: this.relations,
      warehouse_group_id: (
        this.data.warehouse_group_id || EWarehouseDefaultGroup.DEFAULT
      ).toString(),
      rfq_type: this.data.rfq_type || '',
      per_page: '99',
      offset: '0',
      supplierId: this.data?.supplierId?.toString() || '',
      warehouseId: this.data?.warehouseId?.toString(),
    });
  }

  private initForm() :void {
    this.form = this.fb.group({
      defaultQuantity: 1,
      rfqs: this.fb.array([]),
      lineItems: this.fb.array([]),
      client_purchase_order_type_id: [
        this.data?.client_purchase_order_type_id || null,
      ],
    });
  }

  private getFromService() :void {
    this.rfqs$ = this.rfqApiService.rfqs$;
    this.loading$ = this.rfqApiService.loading$;
  }

  private getRfqs() :Observable<any[] | null> {
    return this.rfqs$.pipe(
      tap((rfqs :any[] | null) => {
        this.totalRfq = (rfqs || []).length;
        this.setRfqsItems(rfqs);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private setRfqsItems(rfqs :any) :void {
    this.lineItemsFormArray.clear();
    rfqs.forEach((rfq :any) => {
      const selectedLineItem = this.getSelectedLineItem(rfq.id);
      const rfqItems = this.fb.group({
        ...rfq,
        images: this.fb.array(rfq.images.map((image :any) => this.fb.group(image))),
        quotations: this.fb.array(
          rfq.quotations.map((quotation :any) => this.fb.group(quotation)),
        ),
        rfq_attachments: this.fb.array(
          rfq.rfq_attachments.map((attachment :any) => this.fb.group(attachment)),
        ),
        tags: this.fb.array(rfq.tags.map((tag :any) => this.fb.group(tag))),
        unit_of_measures: this.fb.array(
          rfq.unit_of_measures.map((uom :any) => this.fb.group(uom)),
        ),
        quantity: [selectedLineItem?.quantity || 1],
        selected: [selectedLineItem?.selected || false],
        formattedTags: [this.formattedTags(rfq.tags)],
        thumbnail: [rfq.photo || ''],
        availability: [this.isAvailable(rfq)],
      });
      this.lineItemsFormArray.push(rfqItems);
    });
  }

  private formattedTags(tags :any) :string[] {
    if (tags) {
      return tags.map((tag :any) => tag.tag_details?.name);
    }

    return [];
  }
  private isAvailable(item :any) :boolean {
    if (!item.location_stock) {
      return false;
    }

    const available =
      item.location_stock.qty_onhand - item.location_stock.qty_pending;
    const lineQty = item.quantity;

    if (available <= 0 || lineQty >= available) {
      return false;
    }

    return true;
  }

  private getSelectedLineItem(rfqId :number) :any {
    return this.selectedLineItemsFormArray.value.find(
      (rfq :any) => rfq.id === rfqId,
    );
  }

  private onSearchUpdate() :Observable<ISearchParams> {
    return this.searchUpdated$.asObservable().pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap((params :ISearchParams) => {
        if (this.searchSubscription) {
          // TODO: Move or implement in service
          this.searchSubscription?.unsubscribe();
        }

        if (this.data?.client_id) {
          params['client_id'] = this.data.client_id.toString();
        }

        if (Object.keys(params).length === 0) {
          this.initActions();

          return;
        }

        this.searchSubscription = this.rfqApiService.search({
          ...params,
          warehouse_group_id: (
            this.data.warehouse_group_id || EWarehouseDefaultGroup.DEFAULT
          ).toString(),
          sku: params.sku || '',
          relations: this.relations,
          per_page: '99',
          offset: '0',
          rfq_type: this.data.rfq_type || '',
          sellable: '1',
          supplierId: this.data?.supplierId?.toString() || '',
          warehouseId: this.data?.warehouseId?.toString(),
        });

        this.cd.detectChanges();
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('rfqs') as UntypedFormArray;
  }
  get selectedLineItemsFormArray() :UntypedFormArray {
    return this.form.get('lineItems') as UntypedFormArray;
  }
}
